import { css } from '@emotion/react';
import type { Env0Theme } from 'constants/themes.constants';

interface GetCommonInputCssFromPropsArgs {
  invalid?: boolean;
  theme: Env0Theme;
  fontSize?: number;
  excludeBorderRadius?: boolean;
}

const legacyInputCss = ({ invalid, theme, fontSize, excludeBorderRadius }: GetCommonInputCssFromPropsArgs) => css`
  border: 1px solid ${invalid ? theme.errorRed : theme.lightGray};
  ${excludeBorderRadius ? '' : 'border-radius: 4px !important;'}
  font-size: ${fontSize ? fontSize + 'px' : '12px'} !important;
  font-weight: 500 !important;
  box-shadow: none;
  background-color: ${theme.primaryWhite};

  &:disabled {
    background-color: ${theme.primaryGray} !important;
    border-color: ${theme.lightGray} !important;
    color: ${theme.inputDisabled} !important;
  }
`;

/**
 * Only what the antd Input block in `antd.constants` cannot express: antd has one placeholder colour
 * where the design has three, it reuses `colorBorder` for the disabled border, and `invalid` is our
 * own prop rather than antd's `status`.
 *
 * Doubled class throughout - antd declares each of these on `.ant-input` behind a zero-specificity
 * `:where()`, which a single emotion class only ties.
 *
 * Every rule carries a descendant twin. rc-input hands the className to the input element only for a
 * bare field (`BaseInput` line 73): an addon moves it to `.ant-input-group-wrapper` and a prefix or
 * suffix to `.ant-input-affix-wrapper`, where `::placeholder` matches nothing and the disabled marker
 * is `-group-wrapper-disabled` / `-affix-wrapper-disabled` rather than `ant-input-disabled`. The
 * descendant form is written last so a `:where()`-free antd rule never wins on source order.
 */
const newDesignInputCss = ({ invalid }: GetCommonInputCssFromPropsArgs) => css`
  &&::placeholder,
  && .ant-input::placeholder {
    color: var(--component-field-text-placeholder);
  }

  &&:hover::placeholder,
  &&:hover .ant-input::placeholder {
    color: var(--component-field-text-placeholder-hover);
  }

  /* focus-within, not focus: the wrapper never takes focus itself, the input inside it does. */
  &&:focus::placeholder,
  &&:focus-within .ant-input::placeholder {
    color: var(--component-field-text-placeholder-active);
  }

  &&:disabled,
  &&.ant-input-disabled,
  &&.ant-input-number-disabled,
  &&.ant-input-affix-wrapper-disabled,
  &&.ant-input-group-wrapper-disabled .ant-input,
  &&.ant-input-group-wrapper-disabled .ant-input-group-addon {
    border-color: var(--component-field-border-disabled);
  }

  ${
    invalid &&
    css`
      &&,
      &&:hover,
      &&:focus,
      &&.ant-input-focused,
      &&.ant-input-number-focused,
      &&.ant-input-group-wrapper .ant-input,
      &&.ant-input-group-wrapper .ant-input-group-addon {
        border-color: var(--component-field-border-error);
      }
    `
  }
`;

export const getCommonInputCssFromProps = (args: GetCommonInputCssFromPropsArgs) =>
  args.theme.isNewDesign ? newDesignInputCss(args) : legacyInputCss(args);
